import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * @typedef {{ exceeds_available_quantity: string }} ShippingTranslations
 */

/**
 * Displays a contextual shipping expectation notice on product pages.
 *
 * Translations are scoped to each instance via an embedded script
 *
 * @element shipping-expectation-notice
 *
 * @attr {string} data-product-id       - Shopify product ID for variant update filtering
 * @attr {string} data-block-id         - Theme block ID
 * @attr {string} data-erp-variant-qty  - ERP stock quantity for the selected variant
 * @attr {string} data-erp-variant-available - 'true' | 'false' — whether the variant is ERP-available
 * @attr {string} data-notice-text      - Server-rendered static notice text (used for non-quantity cases)
 */

class ShippingExpectationNotice extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {ShippingTranslations | null} */
  #t = null;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#t = this.#parseTranslations(this);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyUpdate, { signal });
    
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section = null;
    this.#abortController.abort();
  }

   /**
   * Handles variant change events emitted by the section.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const next = event.detail.data.html.querySelector(`shipping-expectation-notice[data-block-id="${this.dataset.blockId}"]`);

    if (!(next instanceof HTMLElement)) return;

    this.dataset.erpVariantQty = next.dataset.erpVariantQty ?? '0';
    this.dataset.erpVariantAvailable = next.dataset.erpVariantAvailable  ?? 'false';
    this.dataset.noticeText = next.dataset.noticeText ?? '';

    const incoming = this.#parseTranslations(next);
    if (incoming) this.#t = incoming;

    this.#currentQuantity = 1;
    morph(this, next, { childrenOnly: true });
    this.#render();
  };

  /**
   * Handles live quantity changes from the product page quantity selector.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQtyUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#render();
  };

  /**
   * Derives the correct notice text from the current component state and
   * updates the DOM. 
   * Falls back to the server-rendered `data-notice-text` for all
   * non-quantity-dynamic cases
   */
  #render() {
    const textEl = this.querySelector('[data-ref="noticeBannerText"]');
    if (!textEl) return;

    const text = this.#resolveText();
    textEl.textContent = text;
    this.classList.toggle('hidden', !text);
  }

  /**
   * Resolves the notice text to display based on current component state.
   * Returns an empty string when the notice should not be shown.
   *
   * @returns {string}
   */
  #resolveText() {
    const erpQty = parseInt(this.dataset.erpVariantQty ?? '0', 10) || 0;
    const isErpAvailable = this.dataset.erpVariantAvailable === 'true';
    const staticText = this.dataset.noticeText ?? '';

    if (isErpAvailable && erpQty > 0 && this.#currentQuantity > erpQty) {
      return this.#translate('exceeds_available_quantity', {
        '[available]': String(erpQty),
        '[remaining]': String(this.#currentQuantity - erpQty),
      });
    }

    return isErpAvailable ? '' : staticText;
  }

  /** 
   * Reads and parses the scoped translations JSON embedded in this element.
   * 
   * @param {Element} el
   * @returns {ShippingTranslations | null}
   */
  #parseTranslations(el) {
    const script = el.querySelector('script[data-shipping-translations]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      console.error('[ShippingExpectationNotice] Failed to parse translations JSON.');
      return null;
    }
  }

  /**
   * Returns a translated string with placeholder substitutions applied.
   *
   * @param {keyof ShippingTranslations} key
   * @param {Record<string, string>} [replacements]
   * @returns {string}
   */
  #translate(key, replacements = {}) {
    if (!this.#t) return '';
    let result = this.#t[key] ?? '';
    for (const [k, v] of Object.entries(replacements)) result = result.split(k).join(v);
    return result;
  }
}

if (!customElements.get('shipping-expectation-notice')) {
  customElements.define('shipping-expectation-notice', ShippingExpectationNotice);
}